/**
 * Signature coordinate bridge — resolve each signatory's text-context anchor
 * (captured at template-build time on the `subKind:'signature'` variable's
 * SourceSpan) to real PDF coordinates in the converted document.
 *
 * The DOCX flow never stores x/y for signatures — only the surrounding text.
 * After DOCX→PDF conversion we load the PDF with pdf.js, flatten each page's
 * text items into one string (tracking which item every character came from),
 * locate the anchor by normalized text + before/after context (same strategy
 * as the docx tokenizer's `locate()`), then union the covering items' rects.
 *
 * Coordinates are returned in PDF points with a TOP-LEFT origin. Feeding SELISE
 * later needs the DPI/origin conversion the PDF e-sign flow already applies.
 */

export interface AnchorInput {
  /** Stable signatory key (the person parent's variable name). */
  signatoryKey: string;
  /** The anchor text captured when the template was marked. */
  originalText: string;
  contextBefore?: string;
  contextAfter?: string;
  paragraphIndex?: number | null;
}

export interface SignatureLocation {
  x: number;
  y: number;
  width: number;
  height: number;
  /** 1-based page number. */
  pageNumber: number;
}

interface CharItem {
  x: number;
  yTop: number;
  width: number;
  height: number;
}

interface PageText {
  pageNumber: number;
  /** Raw concatenated text of the page (items joined with single spaces). */
  raw: string;
  /** For each char index in `raw`, the covering item's rect (null for separators). */
  charItems: (CharItem | null)[];
}

/** Collapse whitespace runs to single spaces, keeping normalized index → raw index. */
function normalize(s: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  let prevSpace = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (/\s/.test(ch)) {
      if (!prevSpace) {
        norm += ' ';
        map.push(i);
        prevSpace = true;
      }
    } else {
      norm += ch;
      map.push(i);
      prevSpace = false;
    }
  }
  return { norm, map };
}

/** Locate the anchor in one page's raw text; returns [rawStart, rawEnd) or null. */
function locateInPage(
  raw: string,
  anchor: AnchorInput
): { rawStart: number; rawEnd: number } | null {
  const { norm: nV, map } = normalize(raw);
  const needle = normalize(anchor.originalText.trim()).norm;
  if (!needle) return null;

  const matches: number[] = [];
  let idx = nV.indexOf(needle);
  while (idx !== -1) {
    matches.push(idx);
    idx = nV.indexOf(needle, idx + 1);
  }
  if (matches.length === 0) return null;

  let chosen: number;
  if (matches.length === 1) {
    chosen = matches[0];
  } else {
    const cb = normalize(anchor.contextBefore ?? '')
      .norm.slice(-16)
      .trimStart();
    const ca = normalize(anchor.contextAfter ?? '')
      .norm.slice(0, 16)
      .trimEnd();
    const scored = matches.map((m) => {
      const before = nV.slice(Math.max(0, m - cb.length), m);
      const after = nV.slice(m + needle.length, m + needle.length + ca.length);
      let score = 0;
      if (cb) score += before.endsWith(cb) ? 2 : before.includes(cb) ? 1 : 0;
      if (ca) score += after.startsWith(ca) ? 2 : after.includes(ca) ? 1 : 0;
      return { m, score };
    });
    scored.sort((a, b) => b.score - a.score);
    // Ambiguous (tie or no context signal) — take the first match rather than fail.
    chosen = scored[0].m;
  }

  const rawStart = map[chosen];
  const rawEnd = map[chosen + needle.length - 1] + 1;
  return { rawStart, rawEnd };
}

/**
 * Fixed signature-box height in PDF points. The detected anchor text is only
 * as tall as its glyphs (~10–12pt), which is too small for a signature, so we
 * force a static height instead.
 *
 * Exported because the DOCX review seeds its boxes from the SAME anchor and
 * must produce the same box the send path will. Any change to anchor box
 * geometry belongs here, so both sides move together — never in one flow.
 */
export const FORCED_ANCHOR_HEIGHT_PT = 32;

/** Union the rects of items covering [rawStart, rawEnd) into one bounding box. */
function boundingBox(page: PageText, rawStart: number, rawEnd: number): SignatureLocation | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  for (let i = rawStart; i < rawEnd && i < page.charItems.length; i++) {
    const it = page.charItems[i];
    if (!it) continue;
    minX = Math.min(minX, it.x);
    minY = Math.min(minY, it.yTop);
    maxX = Math.max(maxX, it.x + it.width);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return {
    x: Math.round(minX),
    y: Math.round(minY),
    width: Math.max(1, Math.round(maxX - minX)),
    // Height is forced to a static box size rather than the detected text height.
    height: FORCED_ANCHOR_HEIGHT_PT,
    pageNumber: page.pageNumber,
  };
}

/**
 * Detect each anchor's location in a PDF, given either its URL or its raw bytes.
 * Returns a map keyed by `signatoryKey`; value is `null` when not found.
 *
 * The editable review flow generates the PDF on the fly at send-time and passes bytes
 * directly (no throwaway URL round-trip); the generate flow still passes a stored URL.
 */
export async function detectSignatureLocations(
  pdf: string | Uint8Array,
  anchors: AnchorInput[]
): Promise<Record<string, SignatureLocation | null>> {
  const source = pdf;
  const result: Record<string, SignatureLocation | null> = {};
  for (const a of anchors) result[a.signatoryKey] = null;
  if (!source || anchors.length === 0) return result;

  const [{ getDocument, GlobalWorkerOptions }, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.mjs?url').then((m) => (m as { default: string }).default),
  ]);
  GlobalWorkerOptions.workerSrc = workerUrl;

  // pdf.js consumes the `data` array (detaches its buffer), so hand it a copy — callers may
  // reuse the same bytes (e.g. also base64-encoding them for the rollout).
  const docParams =
    typeof source === 'string' ? { url: source } : { data: new Uint8Array(source) };
  const loadingTask = getDocument(docParams);
  const pdfDoc = await loadingTask.promise;
  try {
    const pages: PageText[] = [];
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const content = await page.getTextContent();

      let raw = '';
      const charItems: (CharItem | null)[] = [];
      for (const item of content.items as Array<{
        str?: string;
        transform?: number[];
        width?: number;
        height?: number;
      }>) {
        if (typeof item.str !== 'string' || !item.transform) continue;
        const [, , , , e, f] = item.transform;
        const height = item.height ?? 0;
        const rect: CharItem = {
          x: e,
          // pdf.js text origin is bottom-left; convert the glyph top to top-left.
          yTop: viewport.height - f - height,
          width: item.width ?? 0,
          height,
        };
        for (let k = 0; k < item.str.length; k++) {
          raw += item.str[k];
          charItems.push(rect);
        }
        // Separator between items so adjacent words don't fuse.
        raw += ' ';
        charItems.push(null);
      }
      pages.push({ pageNumber: pageNum, raw, charItems });
      page.cleanup();
    }

    for (const anchor of anchors) {
      for (const page of pages) {
        const located = locateInPage(page.raw, anchor);
        if (located) {
          const box = boundingBox(page, located.rawStart, located.rawEnd);
          if (box) {
            result[anchor.signatoryKey] = box;
            break;
          }
        }
      }
      if (!result[anchor.signatoryKey]) {
        console.warn(`Signature anchor not located in PDF for "${anchor.signatoryKey}"`);
      }
    }

    return result;
  } finally {
    await loadingTask.destroy();
  }
}
